import axios, { CancelToken, AxiosRequestConfig, Method } from 'axios';
import get from 'lodash/get';

import getEnv from '@wetransfer/transfer/config/env';
import { AppConfig } from '@wetransfer/transfer/core-bundle/config/app-config';
import Cookie from '@wetransfer/transfer/modules/concorde-cookie';

import {
  processResponse,
  processError,
  CustomResponseError,
  CustomResponse,
} from './response';

interface RequestOptions {
  method?: Method;
  path: string;
  params?: any;
  headers?: Record<string, unknown>;
  timeout?: number;
  onUploadProgress?: (event: Event) => void;
  cancelToken?: CancelToken;
}

/**
 * Map properties passed by the application to properties
 * expected by axios.
 */
function prepareRequest(options: RequestOptions): AxiosRequestConfig {
  const method = get(options, 'method', 'get').toLowerCase() as Method;
  const isGetRequest = method === 'get';

  const urlIsExternal =
    /^https?:/.test(options.path) &&
    !options.path.includes(getEnv().NEXT_PUBLIC_API_URL || '/api/v4');
  const baseHeaders: Record<string, unknown> = {};

  const data = isGetRequest ? null : options.params;
  const params = isGetRequest ? options.params : null;

  const amplitudeDeviceId = AppConfig.amplitude.getAmplitudeDeviceId();

  if (amplitudeDeviceId && !urlIsExternal) {
    baseHeaders['X-Amplitude-Device-Id'] = amplitudeDeviceId;
  }

  if (!urlIsExternal) {
    baseHeaders['X-App-Origin'] = 'decoupled';
  }

  const amplitudeUser = AppConfig.amplitude.user;

  if (amplitudeUser && amplitudeUser.country && !urlIsExternal) {
    baseHeaders['X-Amplitude-Country'] = amplitudeUser.country;
  }

  const amplitudeUserAgent = AppConfig.amplitude.getAmplitudeUserAgentInfo();

  if (amplitudeUserAgent && !urlIsExternal) {
    if (amplitudeUserAgent.deviceType)
      baseHeaders['X-Amplitude-Device-Type'] = amplitudeUserAgent.deviceType;

    if (amplitudeUserAgent.deviceFamily)
      baseHeaders['X-Amplitude-Device-Family'] =
        amplitudeUserAgent.deviceFamily;

    if (amplitudeUserAgent.platform)
      baseHeaders['X-Amplitude-Platform'] = amplitudeUserAgent.platform;
  }

  const languageCookie = Cookie.get(AppConfig.cookies.overrideLanguage.name);

  if (!urlIsExternal) {
    baseHeaders['X-Amplitude-Language'] = languageCookie || 'en';
  }

  const currentTeamCookie = Cookie.get(AppConfig.cookies.currentTeam.name);

  if (currentTeamCookie && !urlIsExternal) {
    baseHeaders['X-Current-Team'] = currentTeamCookie;
  }

  return {
    // Complete, absolute or relative URI where we will send the request
    url: options.path,
    // HTTP method. GET if no method is provided
    method,
    // URL parameters to be sent with a GET request, aka querystring params.
    // Must be a plain object or a URLSearchParams object
    params,
    // The data to be sent as the request body.
    // Only applicable for request methods 'PUT', 'POST', and 'PATCH'
    data,
    // Custom headers to send with the request
    headers: {
      ...baseHeaders,
      ...options.headers,
    },
    // Number of milliseconds before the request times out.
    // If the request takes longer than `timeout`, the request will be aborted.
    timeout: get(options, 'timeout', 0),
    // Allows handling of progress events for uploads
    onUploadProgress: get(options, 'onUploadProgress', null),
    // Specifies a cancel token that can be used to cancel the request
    cancelToken: get(options, 'cancelToken', null),
    // Only 2XX and 3XX status code responses are valid
    validateStatus(status: number) {
      return status >= 200 && status < 400;
    },
  };
}

/**
 * Make a network request using axios. We will map the properties passed by
 * the application to a form expected by axios, and process both success and
 * failed responses to match application expectations.
 */
export default function request(
  options: RequestOptions
): Promise<CustomResponseError | CustomResponse> {
  return axios(prepareRequest(options))
    .then(processResponse)
    .catch(processError);
}
